import { Form } from 'antd';
import { useAppTranslation, useErrTranslation } from 'app/hooks';
import VerticalForm from 'components/Form/VerticalForm';
import React, { useState } from 'react';
import TextField from '../../../components/FormField/TextField';
import { MdEmail } from 'react-icons/md';
import FieldError from 'components/FormField/FieldError';
import { ForgotPasswordFormRequestModel } from 'models/auth';
import authApi from 'api/auth';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleResponseError } from 'hooks/useHandleResponseError';

export enum SendMailType {
  FORGOT_PASSWORD,
  RESEND_ACTIVE_LINK,
}

interface ForgotPasswordProps {
  onSendSuccess: (email: string) => void;
  type: SendMailType;
  titleLabel: string;
  buttonText: string;
  onChangeLoadingStatus: (value: boolean) => void;
  onResendActiveLink?: () => void;
}

const ForgotPassword: React.FunctionComponent<ForgotPasswordProps> = ({
  onSendSuccess,
  type,
  titleLabel,
  buttonText,
  onChangeLoadingStatus,
  onResendActiveLink,
}) => {
  const [disabledSubmit, setDisableSubmit] = useState(false);
  const [form] = Form.useForm();
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();
  const userEmail = Form.useWatch('email', form);
  const et = useErrTranslation();

  const onSendForm = async (value: ForgotPasswordFormRequestModel) => {
    switch (type) {
      case SendMailType.FORGOT_PASSWORD:
        return await authApi.forgotPassword(value);
      case SendMailType.RESEND_ACTIVE_LINK:
        return await authApi.resendActiveLink(value);
    }
  };

  const getSuccessData = () => {
    switch (type) {
      case SendMailType.FORGOT_PASSWORD:
        return {
          title: t('forgotPassword.success.title'),
          content: t('forgotPassword.success.message'),
        };
      case SendMailType.RESEND_ACTIVE_LINK:
        return {
          title: t('resend-active-link.success.title'),
          content: t('resend-active-link.success.message'),
        };
      default:
        return {};
    }
  };

  const onSubmit = async (value: ForgotPasswordFormRequestModel) => {
    setDisableSubmit(true);
    onChangeLoadingStatus(true);
    const { ok, error } = await onSendForm(value);
    onChangeLoadingStatus(false);
    if (ok) {
      const { title, content } = getSuccessData();
      const onOk = () => {
        onSendSuccess(userEmail);
      };
      showSuccessModal({
        title: title,
        content: content,
        onOk,
      });
    }

    if (error) {
      if (error.message === 'error.validate.user.inactive') {
        const onOk = () => {
          onResendActiveLink && onResendActiveLink();
        };
        showErrorModal({
          content: et(error.message),
          onOk,
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
        setDisableSubmit(false);
        return;
      }
      handleResponseError(error);
    }
    setDisableSubmit(false);
  };

  return (
    <VerticalForm
      className="auth__forgot-password"
      name="forgotPassword"
      form={form}
      onFinish={onSubmit}
    >
      <p>{titleLabel}</p>
      <TextField
        name="email"
        placeholder={t('placeholder.enterEmail')}
        prefix={<MdEmail />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.email.required')} />,
          },
          {
            type: 'email',
            message: <FieldError message={t('errors.email.invalid')} />,
          },
        ]}
      />
      <div className="login__footer">
        <button type="submit" className="btn-submit" disabled={disabledSubmit}>
          {buttonText}
        </button>
      </div>
    </VerticalForm>
  );
};

export default ForgotPassword;
